import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OrderResponse } from '../contracts/orders';
import { Button } from '../components/ui/Button';
import { useThemeTokens } from '../themes';
import { createApiClient } from '../lib/api';
import { OrderRow } from '../components/orders/OrderRow';
import { ExportService } from '../lib/export-service';

export const Orders: React.FC = () => {
  const theme = useThemeTokens()
  const navigate = useNavigate()
  const [orders, setOrders] = useState<OrderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Map frontend filter names to API status values
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'active' | 'completed' | 'cancelled'>('all');
  const [downloadingOrders, setDownloadingOrders] = useState<Set<number>>(new Set());
  
  // Prevent unused variable warning - this will be used for loading states
  console.debug('downloadingOrders state:', downloadingOrders.size);



  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const apiClient = await createApiClient();
      const token = localStorage.getItem('token') || 'dev-token-1';
      const ordersData = await apiClient.getOrdersWithFilters(token);
      setOrders(ordersData);
    } catch (err) {
      setError('Failed to load orders');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = () => {
    navigate('/orders/create');
  };

  const handleEditOrder = (order: OrderResponse) => {
    navigate(`/orders/edit/${order.id}`);
  };

  const handleViewOrder = (order: OrderResponse) => {
    navigate(`/orders/view/${order.id}`);
  };

  const handleDownloadPDF = async (order: OrderResponse) => {
    try {
      setDownloadingOrders(prev => new Set([...prev, order.id]));
      
      // Use ExportService to download PDF directly
      await ExportService.downloadPDF(order);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      setError('Failed to download PDF. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setDownloadingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(order.id);
        return newSet;
      });
    }
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    const projectName = order.project?.name || order.title;
    const jobNumber = order.job_number || '';
    const purchaseOrderNumber = order.notes?.replace('PO: ', '') || '';
    
    const matchesSearch = 
      projectName.toLowerCase().includes(searchLower) ||
      order.id.toString().toLowerCase().includes(searchLower) ||
      jobNumber.toLowerCase().includes(searchLower) ||
      purchaseOrderNumber.toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
        <p>Error: {error}</p>
        <Button onClick={loadOrders} className="mt-2">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div 
      className="orders-page" 
      style={{ 
        padding: theme.spacing.xl,
        fontFamily: theme.typography.fontFamily,
        color: theme.colors.text 
      }}
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Orders Management</h1>
          <p className="text-gray-600 mt-1">Manage your environmental project orders</p>
        </div>
        <Button 
          onClick={handleCreateOrder}
          style={{
            backgroundColor: theme.colors.primary,
            color: 'white',
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: theme.borderRadius.md,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.xs
          }}
        >
          + Create New Order
        </Button>
      </div>

      {/* Production Mode Warning */}
      {!import.meta.env.DEV && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-800">
            <span className="text-lg">⚠️</span>
            <div>
              <p className="font-medium">Production Mode - Limited Functionality</p>
              <p className="text-sm text-yellow-700 mt-1">
                Orders functionality is currently in development. 
                For full orders management, please use the development environment.
                This page will show demo data only.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 space-y-4">
        {/* Filter buttons - Updated to match API status values */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded text-sm font-medium ${
              statusFilter === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({orders.length})
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-3 py-1 rounded text-sm font-medium ${
              statusFilter === 'pending' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Pending ({orders.filter(o => o.status === 'pending').length})
          </button>
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-3 py-1 rounded text-sm font-medium ${
              statusFilter === 'active' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Active ({orders.filter(o => o.status === 'active').length})
          </button>
          <button
            onClick={() => setStatusFilter('completed')}
            className={`px-3 py-1 rounded text-sm font-medium ${
              statusFilter === 'completed' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Completed ({orders.filter(o => o.status === 'completed').length})
          </button>
          <button
            onClick={() => setStatusFilter('cancelled')}
            className={`px-3 py-1 rounded text-sm font-medium ${
              statusFilter === 'cancelled' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Cancelled ({orders.filter(o => o.status === 'cancelled').length})
          </button>
        </div>
        
        {/* Search input */}
        <div>
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: `${theme.spacing.xl} 0`,
          color: theme.colors.textSecondary
        }}>
          <div style={{
            fontSize: theme.typography.fontSize.lg,
            marginBottom: theme.spacing.sm
          }}>
            No orders found
          </div>
          <div style={{
            fontSize: theme.typography.fontSize.sm
          }}>
            {orders.length === 0 ? 'No orders exist yet.' : 'Try adjusting your search or filter.'}
          </div>
        </div>
      ) : (
        <div style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.md,
          border: `1px solid ${theme.colors.border}`,
          overflow: 'hidden',
          boxShadow: theme.shadows.sm
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                backgroundColor: theme.colors.background,
                borderBottom: `2px solid ${theme.colors.border}`
              }}>
                <th style={{
                  padding: theme.spacing.md,
                  textAlign: 'left',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text,
                  borderRight: `1px solid ${theme.colors.border}`
                }}>
                  Order & Project
                </th>
                <th style={{
                  padding: theme.spacing.md,
                  textAlign: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text,
                  borderRight: `1px solid ${theme.colors.border}`,
                  width: '120px'
                }}>
                  Status
                </th>
                <th style={{
                  padding: theme.spacing.md,
                  textAlign: 'left',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text,
                  borderRight: `1px solid ${theme.colors.border}`,
                  width: '200px'
                }}>
                  Details
                </th>
                <th style={{
                  padding: theme.spacing.md,
                  textAlign: 'left',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text,
                  borderRight: `1px solid ${theme.colors.border}`,
                  width: '180px'
                }}>
                  Dates
                </th>
                <th style={{
                  padding: theme.spacing.md,
                  textAlign: 'right',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text,
                  borderRight: `1px solid ${theme.colors.border}`,
                  width: '120px'
                }}>
                  Amount
                </th>
                <th style={{
                  padding: theme.spacing.md,
                  textAlign: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text,
                  borderRight: `1px solid ${theme.colors.border}`,
                  width: '200px'
                }}>
                  Preview
                </th>
                <th style={{
                  padding: theme.spacing.md,
                  textAlign: 'center',
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.colors.text,
                  width: '140px'
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  onEdit={() => handleEditOrder(order)}
                  onViewDetails={() => handleViewOrder(order)}
                  onDownloadPDF={() => handleDownloadPDF(order)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}; 